import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, X, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";

interface TypeRow {
    name: string;
    in_use_locations_count: number;
    in_use_inventory_count: number;
}

const LocationTypesAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [types, setTypes] = useState<TypeRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState("");
    const [adding, setAdding] = useState(false);
    const [pendingRemove, setPendingRemove] = useState<TypeRow | null>(null);
    const [removing, setRemoving] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/put-away/location-types`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: TypeRow[] = await res.json();
            setTypes(data);
        } catch {
            toast.error(t("server_error"), { toastId: "loc-types-load-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleAdd = async () => {
        const name = newName.trim().toLowerCase();
        if (!name) return;
        setAdding(true);
        try {
            const res = await apiFetch(`/put-away/location-types`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name }),
            });
            if (res.status === 409) {
                toast.error(t("locations_type_exists"), { toastId: "loc-type-dup" });
                return;
            }
            if (res.status === 400) {
                toast.error(t("locations_type_invalid"), { toastId: "loc-type-bad" });
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setNewName("");
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "loc-type-add-err" });
        } finally {
            setAdding(false);
        }
    };

    const confirmRemove = async () => {
        if (!pendingRemove) return;
        setRemoving(true);
        try {
            const res = await apiFetch(
                `/put-away/location-types/${encodeURIComponent(pendingRemove.name)}`,
                { method: "DELETE" },
            );
            // 409 = catalogue says zero usage but DB found some between the
            // list-fetch and the delete (race / stale list). Re-pull to
            // refresh the counts so the user sees what changed.
            if (res.status === 409) {
                toast.error(t("locations_type_remove_in_use_block"), { toastId: "loc-type-blocked" });
                await refresh();
                setPendingRemove(null);
                return;
            }
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setPendingRemove(null);
            await refresh();
        } catch {
            toast.error(t("server_error"), { toastId: "loc-type-remove-err" });
        } finally {
            setRemoving(false);
        }
    };

    if (loading) {
        return <div className="admin-settings-loading">{t("loading")}</div>;
    }

    return (
        <div className="admin-settings-po-editor">
            <div className="dispatch-admin-rows">
                {types.map(ty => {
                    const inUse = ty.in_use_locations_count + ty.in_use_inventory_count > 0;
                    return (
                        <div key={ty.name} className="dispatch-admin-row dispatch-admin-row--desk">
                            <span className="dispatch-admin-row-name">{ty.name}</span>
                            <span className="dispatch-admin-row-barcode">
                                {t("locations_type_in_use", {
                                    locations: String(ty.in_use_locations_count),
                                    inventory: String(ty.in_use_inventory_count),
                                })}
                            </span>
                            <div className="dispatch-admin-row-actions-inline">
                                <button
                                    type="button"
                                    className="dispatch-admin-row-icon dispatch-admin-row-icon--danger"
                                    onClick={() => setPendingRemove(ty)}
                                    disabled={inUse || readOnly}
                                    aria-disabled={readOnly}
                                    title={inUse ? t("locations_type_remove_blocked_title") : (readOnly ? t("read_only_mode") : t("remove"))}
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    );
                })}
                {types.length === 0 && (
                    <div className="admin-settings-empty">{t("locations_no_types_yet")}</div>
                )}
            </div>
            <div className="dispatch-admin-row-actions" style={{ marginTop: 12, gap: 8 }}>
                <input
                    type="text"
                    className="dispatch-admin-form-input"
                    style={{ flex: "1 1 200px", maxWidth: 280 }}
                    placeholder={t("locations_type_name_placeholder")}
                    value={newName}
                    maxLength={64}
                    onChange={e => setNewName(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
                    disabled={adding || readOnly}
                />
                <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleAdd}
                    disabled={adding || !newName.trim() || readOnly}
                    aria-disabled={readOnly}
                    title={readOnly ? t("read_only_mode") : undefined}
                >
                    {adding ? <Loader2 size={14} className="dispatch-admin-spin" /> : <Plus size={14} />}
                    {t("locations_type_add")}
                </button>
            </div>

            {pendingRemove && (
                <div
                    className="modal-overlay"
                    onClick={() => !removing && setPendingRemove(null)}
                >
                    <div
                        className="modal-card locations-delete-modal"
                        onClick={e => e.stopPropagation()}
                        role="alertdialog"
                        aria-labelledby="loc-type-remove-title"
                    >
                        <div className="modal-card-header">
                            <h2 id="loc-type-remove-title">{t("locations_type_remove_title")}</h2>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={() => setPendingRemove(null)}
                                disabled={removing}
                                aria-label={t("close")}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="modal-card-body">
                            <p className="locations-delete-body">
                                {t("locations_type_remove_body", { name: pendingRemove.name })}
                            </p>
                            {(pendingRemove.in_use_locations_count > 0 || pendingRemove.in_use_inventory_count > 0) && (
                                <p
                                    className="admin-settings-section-hint admin-settings-section-hint--danger"
                                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                                >
                                    <AlertTriangle size={16} />
                                    {t("locations_type_remove_blocked", {
                                        locations: String(pendingRemove.in_use_locations_count),
                                        inventory: String(pendingRemove.in_use_inventory_count),
                                    })}
                                </p>
                            )}
                            <p className="locations-delete-hint">{t("locations_type_remove_hint")}</p>
                        </div>
                        <div className="modal-confirm-buttons">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={() => setPendingRemove(null)}
                                disabled={removing}
                            >
                                {t("cancel")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-danger"
                                onClick={confirmRemove}
                                disabled={
                                    removing
                                    || pendingRemove.in_use_locations_count > 0
                                    || pendingRemove.in_use_inventory_count > 0
                                    || readOnly
                                }
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                <Trash2 size={16} />
                                {removing ? t("submitting") : t("remove")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LocationTypesAdmin;
